import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function ProfilePage() {
  const navigate = useNavigate();
  const [showCodeInput, setShowCodeInput] = useState(true);
  const [accessCode, setAccessCode] = useState('');
  const [codeError, setCodeError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_number: '',
    date_of_birth: '',
    parent1_number: '',
    parent2_number: ''
  });
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setCodeError(null);
    setLoading(true);

    try {
      if (!supabase) {
        throw new Error('Database connection not configured');
      }

      // Case-insensitive match so "Ali20" in DB matches "ali20" or "Ali20"
      const codeTrimmed = (accessCode || '').trim();
      const { data, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .ilike('code', codeTrimmed)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (!data) {
        setCodeError('Invalid access code');
        setLoading(false);
        return;
      }

      setUserData(data);
      setFormData({
        name: data.name || '',
        email: data.email || '',
        phone_number: data.phone_number || '',
        date_of_birth: data.date_of_birth || '',
        parent1_number: data.parent1_number || '',
        parent2_number: data.parent2_number || ''
      });
      setShowCodeInput(false);
    } catch (err) {
      setCodeError(err.message || 'Invalid access code');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (success) setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      if (!userData) {
        throw new Error('User data not loaded');
      }

      // Validate email format if provided
      const emailValue = formData.email.trim();
      if (emailValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
        throw new Error('Please enter a valid email address');
      }

      const cleanedData = {
        name: formData.name.trim(),
        email: emailValue || null,
        phone_number: formData.phone_number.trim() || null,
        date_of_birth: formData.date_of_birth || null,
        parent1_number: formData.parent1_number.trim().substring(0, 20) || null,
        parent2_number: formData.parent2_number.trim().substring(0, 20) || null,
        updated_at: new Date().toISOString()
      };

      if (!cleanedData.name) {
        throw new Error('Name is required');
      }

      const { error: updateError } = await supabase
        .from('users')
        .update(cleanedData)
        .eq('id', userData.id);

      if (updateError) throw updateError;

      setSuccess(true);
      setUserData({ ...userData, ...cleanedData });
      
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToCode = () => {
    setShowCodeInput(true);
    setUserData(null);
    setAccessCode('');
    setFormData({
      name: '',
      email: '',
      phone_number: '',
      date_of_birth: '',
      parent1_number: '',
      parent2_number: ''
    });
    setError(null);
    setSuccess(false);
  };

  if (showCodeInput) {
    return (
      <div className="elegant-font min-h-screen bg-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="card p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-black mb-2">Update Your Profile</h1>
              <p className="text-gray-600">Enter your access code to update your information</p>
            </div>

            {codeError && (
              <div className="mb-6 p-4 bg-gray-100 border border-black rounded">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-black text-sm">{codeError}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleCodeSubmit} className="space-y-6">
              <div>
                <label htmlFor="accessCode" className="block text-sm font-medium text-black mb-2">
                  Access Code
                </label>
                <input
                  id="accessCode"
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  className="input text-center text-lg"
                  placeholder="Enter your access code"
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                className="btn-primary w-full"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block mr-2"></div>
                    Loading...
                  </>
                ) : (
                  'Continue'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/')}
                className="text-sm text-gray-600 hover:text-black"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="elegant-font min-h-screen bg-white">
      <header className="border-b border-gray-200">
        <div className="container-mobile py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackToCode}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
              >
                <svg className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-xl sm:text-2xl font-bold text-black">Update Profile</h1>
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-600 hover:text-black"
            >
              Home
            </button>
          </div>
        </div>
      </header>

      <main className="container-mobile py-8 max-w-2xl mx-auto">
        {error && (
          <div className="mb-6 p-4 bg-gray-100 border border-black rounded">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-black text-sm">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-gray-50 border border-gray-300 rounded">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-black text-sm font-medium">Profile updated successfully!</p>
            </div>
          </div>
        )}

        <div className="card p-6">
          <div className="mb-6">
            <h2 className="text-lg font-bold text-black mb-2">Personal Information</h2>
            <p className="text-sm text-gray-600">Update your personal details and contact information</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-black mb-2">
                Full Name <span className="text-gray-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="input"
                placeholder="Enter your full name"
                required
              />
            </div>

            {/* Username */}
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-black mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="input"
                placeholder="Enter your email address (e.g., name@example.com)"
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
              />
              <p className="mt-1 text-xs text-gray-500">Update your email address for notifications and account recovery</p>
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phone_number" className="block text-sm font-medium text-black mb-2">
                Phone Number
              </label>
              <input
                id="phone_number"
                type="tel"
                value={formData.phone_number}
                onChange={(e) => handleInputChange('phone_number', e.target.value)}
                className="input"
                placeholder="Enter your phone number"
              />
              <p className="mt-1 text-xs text-gray-500">Your personal contact number</p>
            </div>

            {/* Date of Birth */}
            <div>
              <label htmlFor="date_of_birth" className="block text-sm font-medium text-black mb-2">
                Date of Birth
              </label>
              <input
                id="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                className="input"
              />
              <p className="mt-1 text-xs text-gray-500">Optional - your date of birth</p>
            </div>

            {/* Parent Phone Numbers - Only for Students */}
            {userData?.role === 'student' && (
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-base font-semibold text-black mb-4">Parent/Guardian Information</h3>
                
                <div className="space-y-6">
                  <div>
                    <label htmlFor="parent1_number" className="block text-sm font-medium text-black mb-2">
                      Primary Parent/Guardian Phone
                    </label>
                    <input
                      id="parent1_number"
                      type="tel"
                      value={formData.parent1_number}
                      onChange={(e) => handleInputChange('parent1_number', e.target.value)}
                      className="input"
                      placeholder="Enter primary parent phone number"
                      maxLength={20}
                    />
                    <p className="mt-1 text-xs text-gray-500">Used for parent lookup feature (max 20 characters)</p>
                  </div>

                  <div>
                    <label htmlFor="parent2_number" className="block text-sm font-medium text-black mb-2">
                      Secondary Parent/Guardian Phone
                    </label>
                    <input
                      id="parent2_number"
                      type="tel"
                      value={formData.parent2_number}
                      onChange={(e) => handleInputChange('parent2_number', e.target.value)}
                      className="input"
                      placeholder="Enter secondary parent phone number"
                      maxLength={20}
                    />
                    <p className="mt-1 text-xs text-gray-500">Optional - additional contact (max 20 characters)</p>
                  </div>
                </div>
              </div>
            )}

            {/* Read-only fields */}
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-base font-semibold text-black mb-4">Account Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Access Code
                  </label>
                  <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700">
                    {userData?.code || 'Not set'}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Your unique access code (cannot be changed)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Role
                  </label>
                  <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700 capitalize">
                    {userData?.role || 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleBackToCode}
                className="btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block mr-2"></div>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
